import React, { useState, useEffect } from "react";
import { styled } from "@linaria/react";
import { css } from "@linaria/core";
import { PLANS_NAME } from "@static/config";
import { CONTENT_VIEW_QUOTATION_DISCLAIMER, STEPS_LIST } from "@static/content";
import Button from "@components/_Revamp/Button";
import { useModalView } from "src/Modal/modal";
import { MODALVIEW } from "src/Modal/Modal-View-Config/modal-view-config";
import { useHistory } from "react-router-dom";
import { ROUTE_PATH } from "@static/route-paths";
import { useAvivaSelector } from "@redux/hook";
import { themeDefault } from "src/theme-revamp";
import { Card } from "@components/_Revamp/StyledComponent";
import infoIconBlack from "@assets/icon/info-black.svg";
import MainContainer from "@pages/ShieldStarter/Components/Container";
import { trackOnLoad } from "@pages/ShieldStarter/Helpers/dataAnalyticsHelpers";
import { SCREEN_SIZE } from "@static/screen-size";
import { get } from "lodash";
import { IRawQuotation } from "@redux/slices/SHIELD_STARTER/step2Slice";

const anchorTag = css`
  text-decoration: underline;
  color: #883E89;
  font-weight: 600;
`;

const ulStyle = css`
  font-family: "Open Sans";
  font-style: normal;
  list-style-type: disc;
  width: 96%;
  text-align: justify;
  margin-top: 50px;
  line-height: 180%;
  & li {
    margin-bottom: 20px;
  }

  @media screen and (max-width: 768px) {
    width: 90%;
  }
`;

const BoldText = styled.p`
  margin-top: 30px;
  font-style: normal;
  display: flex;
  justify-content: space-between;
  font-weight: 600;
  cursor: pointer;
  & > button {
    outline: none;
    cursor: pointer;
    background: none;
    border: none;
  }
`;

const DisclaimerButton = styled.svg<{ closeDisclaimer: boolean }>`
  transform: rotate(${(props) => (props.closeDisclaimer ? "180deg" : "0deg")});
`;

const DisclosureContainer = styled.div<{ closeDisclaimer: boolean }>`
  font-family: "Open Sans";
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 140%;
  height: ${(props) => (props.closeDisclaimer ? "0" : "fit-content")};
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: ${(props) => (props.closeDisclaimer ? "0" : "400px")};
  transition: min-height 0.5s ease;
  margin-bottom: 20px;

  & > p {
    font-family: "Open Sans";
    font-style: normal;
    font-weight: 400;
    font-size: 14px;
    line-height: 140%;
    width: 96%;
  }

  @media screen and (max-width: 768px) {
    min-height: ${(props) => (props.closeDisclaimer ? "0px" : "1050px")};
    & > p {
      width: 90%;
    }
  }
`;

const Header = styled.div`
  font-family: "Open Sans";
  font-style: normal;
  font-weight: 600;
  font-size: 18px;
  text-align: center;
  line-height: 140%;

  @media (min-width: ${SCREEN_SIZE.mobilesUp}px) {
    font-size: 28px;
  }
`;

const SpiltUpContainer = styled.div<{ mariginTop?: string }>`
  width: 100%;
  margin-top: ${(props) => props.mariginTop || "60px"};
`;

const Description = styled.p`
  text-align: center;
  font-weight: 400;
  font-size: 14px;
  line-height: 140%;
  color: #8a8988;
  margin-top: 16px;
  @media (min-width: ${SCREEN_SIZE.mobilesUp}px) {
    font-weight: 600;
    margin-bottom: 24px;
  }
`;

const SplitupLabel = styled.span<{
  color?: string;
  fontSize?: number;
  fontWeight?: number;
}>`
  color: ${(props) => props.color || themeDefault.colors.grey["500"]};
  font-weight: ${(props) => props.fontWeight || 400};
  font-size: ${(props) => (props.fontSize === 18 ? 16 : 14)}px;
  @media (min-width: ${SCREEN_SIZE.mobilesUp}px) {
    font-size: ${(props) => props.fontSize || 14}px;
  }
  & > div {
    font-weight: 400;
  }
`;

const PromoEdit = styled.button<{ color: string }>`
  border: none;
  cursor: pointer;
  background: none;
  font-weight: 600;
  color: ${(props) => props.color};
  font-family: "Open Sans";
  font-style: normal;
  font-weight: 600;
  font-size: 16px;
  line-height: 140%;
  text-decoration-line: underline;
`;

const SplitupValue = styled.span<{
  fontWeight?: string;
  color?: string;
  width?: string;
  fontSize?: string;
}>`
  width: ${(prop) => prop.width || "auto"};
  text-align: right;
  font-weight: ${(props) => props.fontWeight || 600};
  color: ${(props) => props.color || themeDefault.colors.grey["500"]};
  font-size: ${(props) => (props.fontSize === "18px" ? 16 : 14)}px;

  @media (min-width: ${SCREEN_SIZE.mobilesUp}px) {
    font-size: ${(props) => props.fontSize || "14px"};
  }
`;

const SplitupHeader = styled.div`
  align-self: flex-start;
  font-weight: 600;
  font-size: 16px;
  margin-top: 48px;
  margin-bottom: 16px;
  @media (min-width: ${SCREEN_SIZE.mobilesUp}px) {
    font-size: 18px;
  }
`;

const SplitupComponent = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  justify-content: space-between;
  padding-bottom: 16px;
  color: #434343;
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 140%;
`;

const Notes = styled.ul<{ fontSize?: number }>`
  width: 80%;
  font-size: ${(props) => props.fontSize || themeDefault.fontSize.text}px;
  font-family: "Open Sans";
  font-style: normal;
  font-weight: 500;
  line-height: 140%;
  flex-grow: 1;
  margin-left: 20px;
  & > div {
    margin-bottom: 5px;
    font-weight: 600;
    & > span {
      color: #883E89;
      border-bottom: solid 1px;
    }
  }
`;

const PromocodeText = styled.div`
  font-style: normal;
  font-weight: 600;
  font-size: 14px;
  line-height: 140%;
  & > span {
    font-family: "Open Sans";
    font-style: normal;
    font-weight: 400;
    font-size: 14px;
    line-height: 19.6px;
  }
`;

const BackButton = styled.button`
  border: none;
  cursor: pointer;
  background: none;
  display: flex;
  font-size: 16px;
  font-weight: 600;
  position: relative;
  left: -24px;
  align-items: center;
  & > svg {
    margin-right: 10px;
  }
`;

function ViewQuotation() {
  const [closeDisclaimer, setCloseDisclaimer] = useState(false);
  const history = useHistory();
  const [modal, setModalState] = useModalView();
  const choosePlan = useAvivaSelector((state) => state.shieldStarterStep2);
  const planOverview = useAvivaSelector(
    (state) => state.shieldStarterStep2.planOverview
  );

  //TODO: clean this useEffect once Choose plan page is implemented
  useEffect(() => {
    if (!planOverview && !planOverview?.PlanOverViewData) {
      onBack();
    } else {
      trackOnLoad("sss_view_quotation_page");
    }
    return () => {
      setModalState({
        isModalShow: false,
        ModalViewComponent: "",
      });
    };
  }, []);

  function showPromoCodeModal() {
    setModalState({
      isModalShow: true,
      ModalViewComponent: MODALVIEW.SHIELD_STARTER_PROMOCODE,
    });
  }

  function onBack() {
    history.replace(ROUTE_PATH.SHIELD_STARTER_CHOOSE_PLAN);
  }

  return (
    <MainContainer
      step={2}
      backButtonPath={ROUTE_PATH.SHIELD_STARTER_CHOOSE_PLAN}
    >
      <Card marginTop="0">
        <Header>Here’s your quote</Header>
        <Description>
          Check your plan details before we start the application
        </Description>
        <SpiltUpContainer mariginTop="0px">
          <SplitupHeader>Plan Overview</SplitupHeader>
          <SplitupComponent>
            <SplitupLabel>Plan Selected</SplitupLabel>
            <SplitupValue width="155px">
              {planOverview?.PlanOverViewData?.selectedPlanName || ""}
            </SplitupValue>
          </SplitupComponent>
          <SplitupComponent>
            <SplitupLabel>Policy Term</SplitupLabel>
            <SplitupValue>{"Yearly Renewable"}</SplitupValue>
          </SplitupComponent>
          <SplitupComponent>
            <SplitupLabel>Premium Term</SplitupLabel>
            <SplitupValue>{"Yearly Renewable"}</SplitupValue>
          </SplitupComponent>
          <SplitupHeader>Annual Premium Breakdown*</SplitupHeader>
          <SplitupComponent>
            <SplitupLabel>
              {planOverview?.PlanOverViewData?.basePlan?.name}
            </SplitupLabel>
            <SplitupValue>
              S${planOverview?.PlanOverViewData?.basePlanPriceNoGst}
            </SplitupValue>
          </SplitupComponent>
          {planOverview?.PlanOverViewData?.riderPlan && (
            <SplitupComponent>
              <SplitupLabel>
                <li>
                  {planOverview?.PlanOverViewData?.riderPlan?.name ||
                    "Rider Name"}
                </li>
              </SplitupLabel>
              <SplitupValue>
                {planOverview?.PlanOverViewData?.riderPlanPriceNoGst
                  ? `S$${planOverview?.PlanOverViewData?.riderPlanPriceNoGst}`
                  : "-"}
              </SplitupValue>
            </SplitupComponent>
          )}
          <SplitupComponent>
            <SplitupLabel>{"CPF MediSave Deduction"}</SplitupLabel>
            <SplitupValue>
              -S${planOverview?.PlanOverViewData?.MediSaveDeduction}
            </SplitupValue>
          </SplitupComponent>
          <SplitupComponent style={{ marginBottom: "24px" }}>
            <SplitupLabel>
              <div>
                Promo Code:{" "}
                <PromoEdit
                  color={themeDefault.colors.primary}
                  onClick={showPromoCodeModal}
                >
                  Edit
                </PromoEdit>
              </div>
              {planOverview?.PlanOverViewData?.promoCode &&
                planOverview?.PlanOverViewData?.basePlan?.attributes
                  ?.discountEligible && (
                  <div>
                    <PromocodeText>
                      {` ${planOverview?.PlanOverViewData?.promoCode.toUpperCase()} `}{" "}
                      {planOverview?.PlanOverViewData?.promoCode
                        .toUpperCase()
                        .endsWith("GST") ? (
                        <span>(GST waiver for first year)</span>
                      ) : (
                        ""
                      )}
                    </PromocodeText>
                  </div>
                )}
            </SplitupLabel>
            <SplitupValue>
              {(planOverview?.PlanOverViewData?.promoCodeDiscount &&
                planOverview?.PlanOverViewData?.basePlan?.attributes
                  ?.discountEligible &&
                `-S$${planOverview?.PlanOverViewData?.promoCodeDiscount}`) ||
                "-"}
            </SplitupValue>
          </SplitupComponent>
          <hr />
          <SplitupComponent style={{ marginTop: "48px", paddingBottom: "0px" }}>
            <SplitupLabel
              color={themeDefault.colors.grey[500]}
              fontWeight={600}
              fontSize={18}
            >
              {" "}
              Cash Payment
              <div
                style={{
                  fontSize: "12px",
                  lineHeight: "140%",
                  fontWeight: 500,
                }}
              >
                (after CPF MediSave Deduction)
              </div>
            </SplitupLabel>
            <SplitupValue color="#883E89" fontSize="18px">
              {planOverview?.PlanOverViewData?.totalAmount
                ? `S$${planOverview?.PlanOverViewData?.totalCashPayble}`
                : "-"}
            </SplitupValue>
          </SplitupComponent>
          <SplitupComponent style={{ marginTop: "48px", padding: "0px" }}>
            <SplitupLabel fontWeight={400} fontSize={12}>
              *Premiums quoted after GST
            </SplitupLabel>
          </SplitupComponent>
        </SpiltUpContainer>
      </Card>
      <Card>
        <SplitupComponent style={{ padding: "0px " }}>
          <div>
            <img
              width="18px"
              height="18px"
              src={infoIconBlack}
              alt={"info-icon"}
            />
          </div>
          <Notes fontSize={12}>
            <li>Premiums are not guaranteed and may be revised annually.</li>
            <li>
              Check the premium affordability using the{" "}
              <a
                className={anchorTag}
                href={get(
                  window,
                  "AVIVA_VARIABLE_CONFIG.budgetCalculatorUrl",
                  ""
                )}
                target="_blank"
                rel="noreferrer noopener"
              >
                budget calculator
              </a>
              .
            </li>
          </Notes>
        </SplitupComponent>
      </Card>
      <Button
        onClick={() => {
          history.push(ROUTE_PATH.SHIELD_STARTER_START_APPLICATION);
        }}
        margin={"30px auto"}
        display="block"
        width="292px"
        label="Start my application"
      />
      <div>
        <hr />
        <BoldText
          onClick={() => {
            setCloseDisclaimer((p) => !p);
          }}
        >
          A few points to note before buying
          <button>
            <DisclaimerButton
              closeDisclaimer={closeDisclaimer}
              width="16"
              height="9"
              viewBox="0 0 16 9"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M1.17557 8.33398C0.618752 8.33398 0.3399 7.66077 0.733628 7.26704L7.55808 0.442591C7.80215 0.198513 8.19788 0.198513 8.44196 0.442591L15.2664 7.26704C15.6601 7.66077 15.3813 8.33398 14.8245 8.33398H1.17557Z"
                fill={themeDefault.colors.grey["500"]}
              />
            </DisclaimerButton>
          </button>
        </BoldText>
        <DisclosureContainer closeDisclaimer={closeDisclaimer}>
          <ul className={ulStyle}>
            {CONTENT_VIEW_QUOTATION_DISCLAIMER &&
              CONTENT_VIEW_QUOTATION_DISCLAIMER[
                PLANS_NAME.SHIELD_STARTER
              ]?.disclaimers.map((entry, i) => {
                return <li key={"dis" + i}>{entry}</li>;
              })}
          </ul>
          {CONTENT_VIEW_QUOTATION_DISCLAIMER &&
            CONTENT_VIEW_QUOTATION_DISCLAIMER[
              PLANS_NAME.SHIELD_STARTER
            ]?.legends.map((entry, i) => {
              return <p key={"legend" + i}>{entry}</p>;
            })}
        </DisclosureContainer>
        <hr />
      </div>
    </MainContainer>
  );
}

export default ViewQuotation;
